import {
  calculateSubscriptionPrice,
  calculateTotalPrice,
  formatCurrency,
} from "@/lib/utils";

type ProductLineTotalsInput = {
  originalPrice: number;
  displayPrice: number;
  quantity: number;
  subscribeAndSave?: string | null;
  lineTotalInCurrency?: string | null;
  lineTotalAfterDiscountsInCurrency?: string | null;
  currencySymbol: string;
  currencyDecimalPlaces?: number;
  currencyCode?: string;
  isFree?: boolean;
};

type ProductLineTotals = {
  originalLineTotal: string;
  currentLineTotal: string;
};

const presentCurrencyValue = (value: string | null | undefined) =>
  value?.trim() || null;

export function productLineTotals({
  originalPrice,
  displayPrice,
  quantity,
  subscribeAndSave,
  lineTotalInCurrency,
  lineTotalAfterDiscountsInCurrency,
  currencySymbol,
  currencyDecimalPlaces,
  currencyCode,
  isFree = false,
}: ProductLineTotalsInput): ProductLineTotals {
  const currencyFormatOptions = {
    symbol: currencySymbol,
    decimalPlaces: currencyDecimalPlaces,
    currencyCode,
  };
  const originalTotal = calculateTotalPrice(originalPrice, quantity);
  const hasSubscriptionDiscount =
    subscribeAndSave != null && Number(subscribeAndSave) > 0;
  const currentTotal = hasSubscriptionDiscount
    ? calculateSubscriptionPrice(
        Number(originalTotal),
        subscribeAndSave,
        quantity,
      )
    : calculateTotalPrice(displayPrice, quantity);
  const backendOriginalLineTotal = presentCurrencyValue(lineTotalInCurrency);
  const backendCurrentLineTotal = presentCurrencyValue(
    lineTotalAfterDiscountsInCurrency,
  );

  return {
    originalLineTotal:
      backendOriginalLineTotal ??
      formatCurrency(Number(originalTotal), currencyFormatOptions),
    // A free line is never charged, so it never reuses the original total the
    // way a non-discounted paid line does — it resolves to the backend
    // after-discount total, otherwise zero.
    currentLineTotal: isFree
      ? (backendCurrentLineTotal ?? formatCurrency(0, currencyFormatOptions))
      : (backendCurrentLineTotal ??
        // No subscription discount means the current total equals the
        // original, so a present backend original can stand in for it.
        (hasSubscriptionDiscount ? null : backendOriginalLineTotal) ??
        formatCurrency(Number(currentTotal), currencyFormatOptions)),
  };
}
